import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MobilePartnerBanner } from "../components/MobilePartnerBanner";
import { Search } from "lucide-react";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";

export default function Institutes() {
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ cities: [], areas: [], exams: [] });
  const [filters, setFilters] = useState({ q: params.get("q") || "", city: params.get("city") || "", area: params.get("area") || "", type: params.get("type") || "", exam: params.get("exam") || "" });
  const [sort, setSort] = useState("points");
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async (f) => {
    setLoading(true);
    const query = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try {
      const { data } = await api.get("/institutions", { params: query });
      setList(data);
    } catch { setList([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchList(filters);
    api.get("/institutions/cities").then((r) => setMeta(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const f = { q: params.get("q") || "", city: params.get("city") || "", area: params.get("area") || "", type: params.get("type") || "", exam: params.get("exam") || "" };
    setFilters(f); fetchList(f);
  }, [params, fetchList]);

  const update = (patch) => {
    const f = { ...filters, ...patch };
    setFilters(f);
    fetchList(f);
  };

  const sorted = [...list].sort((a, b) => {
    if (sort === "rating") return b.avg_rating - a.avg_rating;
    if (sort === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
    return b.total_score - a.total_score;
  });

  return (
    <div className="institutes-page max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="nb-label" style={{ letterSpacing: "0.2em" }}>Discover</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Coaching Institutes & Schools</h1>

      <div className="mobile-directory-tabs md:hidden" data-testid="mobile-directory-tabs">{[["", "All"], ["coaching", "Coaching Institutes"], ["school", "K–12 Schools"], ["college", "Colleges"]].map(([key, label]) => <button data-testid={`directory-type-${key || 'all'}`} key={key} onClick={() => update({ type: key })} aria-pressed={filters.type === key}>{label}</button>)}<Link data-testid="directory-teachers" to="/teachers">Teachers</Link><Link data-testid="directory-tuition" to="/home-tuition">Home Tuition</Link></div>
      <div className="directory-filter-grid mt-8 grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="relative md:col-span-5">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="home-search-input" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && fetchList(filters)}
            onBlur={() => fetchList(filters)}
            placeholder="Search..." className="nb-input" style={{ paddingLeft: 42 }} />
        </div>
        <select data-testid="home-city-filter" value={filters.city} onChange={(e) => update({ city: e.target.value })} className="nb-input md:col-span-2">
          <option value="">All cities</option>
          {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="home-area-filter" value={filters.area} onChange={(e) => update({ area: e.target.value })} className="nb-input md:col-span-2">
          <option value="">All areas</option>
          {meta.areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="home-type-filter" value={filters.type} onChange={(e) => update({ type: e.target.value })} className="nb-input md:col-span-3">
          <option value="">All categories</option>
          <option value="coaching">Coaching Institutes</option>
          <option value="school">Schools</option>
          <option value="college">Colleges</option>
        </select>
        <select data-testid="home-exam-filter" value={filters.exam} onChange={(e) => update({ exam: e.target.value })} className="nb-input md:col-span-3">
          <option value="">All exams</option>
          {(meta.exams || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <span className="nb-label">Sort:</span>
        <select data-testid="institutes-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="nb-input" style={{ width: "auto" }}>
          <option value="points">Points (Top ranked)</option>
          <option value="rating">Rating</option>
          <option value="newest">Newest</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto">{sorted.length} listed</span>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-500">Loading...</div>
      ) : sorted.length === 0 ? (
        <div data-testid="institutes-empty-state" className="nb-flat mt-8 p-14 text-center border-dashed">
          <p className="font-bold">No institutions match your filters</p>
          <p className="text-sm text-slate-500 mt-1">Try clearing filters or search for something else.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {sorted.map((inst, i) => <ListingCard key={inst.id} inst={inst} index={i} />)}
        </div>
      )}
      <div className="md:hidden mt-8"><MobilePartnerBanner title="Are you an institute owner?" /></div>
    </div>
  );
}
